import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * useCustomViewActions - Manages state and handlers for custom view interactions
 */
export function useCustomViewActions({
  onAddCustomView,
  onSelectCustomView,
  onRenameCustomView,
  onDuplicateCustomView,
  onDeleteCustomView,
  onViewChange,
}) {
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [editingViewId, setEditingViewId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const addMenuRef = useRef(null);
  const contextMenuRef = useRef(null);
  const editInputRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setIsAddMenuOpen(false);
      }
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when editing
  useEffect(() => {
    if (editingViewId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingViewId]);

  // Handle adding a new custom view
  const handleAddView = useCallback((type) => {
    if (onAddCustomView) {
      // addView returns the new view object with its ID
      const newView = onAddCustomView(type);
      // Select the new view (this handles URL update and history)
      if (newView?.id && onSelectCustomView) {
        onSelectCustomView(newView.id);
      }
    }
    setIsAddMenuOpen(false);
  }, [onAddCustomView, onSelectCustomView]);

  // Handle clicking a default view tab
  const handleDefaultViewClick = useCallback((viewType) => {
    if (onSelectCustomView) {
      onSelectCustomView(null);
    }
    onViewChange(viewType);
  }, [onSelectCustomView, onViewChange]);

  // Handle clicking a custom view tab
  // Note: onSelectCustomView handles everything - setting activeCustomViewId, 
  // updating toolbar view type, and updating URL. We don't call onViewChange
  // separately to avoid double history entries.
  const handleCustomViewClick = useCallback((view) => {
    if (onSelectCustomView) {
      onSelectCustomView(view.id);
    }
    // Don't call onViewChange - selectViewWithUrlSync already handles:
    // 1. Setting activeCustomViewId
    // 2. Setting toolbar.currentView
    // 3. Updating URL with customViewId
  }, [onSelectCustomView]);

  // Handle right-click on custom view tab
  const handleCustomViewContextMenu = useCallback((e, view) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      view,
    });
  }, []);

  // Start editing a view name
  const startEditing = useCallback((view) => {
    setEditingViewId(view.id);
    setEditingName(view.name);
    setContextMenu(null);
  }, []);

  // Save edited name
  const saveEditedName = useCallback(() => {
    if (editingViewId && editingName.trim() && onRenameCustomView) {
      onRenameCustomView(editingViewId, editingName.trim());
    }
    setEditingViewId(null);
    setEditingName('');
  }, [editingViewId, editingName, onRenameCustomView]);

  // Handle key press in edit input
  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      saveEditedName();
    } else if (e.key === 'Escape') {
      setEditingViewId(null);
      setEditingName('');
    }
  }, [saveEditedName]);

  // Handle delete button click (shows confirmation modal)
  const handleDeleteClick = useCallback((e, view) => {
    e.stopPropagation();
    setDeleteConfirm({ viewId: view.id, viewName: view.name });
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(() => {
    if (deleteConfirm && onDeleteCustomView) {
      const newActiveView = onDeleteCustomView(deleteConfirm.viewId);
      // If a new view was returned (deleted view was active), switch to its type
      if (newActiveView) {
        onViewChange(newActiveView.type);
      }
    }
    setDeleteConfirm(null);
  }, [deleteConfirm, onDeleteCustomView, onViewChange]);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setDeleteConfirm(null);
  }, []);

  // Toggle add menu
  const toggleAddMenu = useCallback(() => {
    setIsAddMenuOpen(prev => !prev);
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  return {
    // State
    isAddMenuOpen,
    contextMenu,
    editingViewId,
    editingName,
    deleteConfirm,
    // Refs
    addMenuRef,
    contextMenuRef,
    editInputRef,
    // Handlers
    handleAddView,
    handleDefaultViewClick,
    handleCustomViewClick,
    handleCustomViewContextMenu,
    startEditing,
    saveEditedName,
    handleEditKeyDown,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    toggleAddMenu,
    closeContextMenu,
    setEditingName,
  };
}

export default useCustomViewActions;
